import { EntityStatePayload } from '@common/api';
import { GetDefaults } from '@common/components/ComponentBase';

import { ClientWorld } from '../ClientWorld';
import { ClientDefaults, ClientRegistry } from '../components';
import { RenderService } from './RenderService';

export type ComponentDataTranslator = (data: unknown, clientEntityId: number) => unknown;
export type EntityCreationListener = (serverEntityId: number, clientEntityId: number) => void;
export type WorldResetListener = () => void;

/**
 * Service wrapping ClientWorld.
 * Client systems use this to get the typed ClientWorld.
 *
 * Supports generic component translations for resolving server entity IDs to client entity IDs,
 * allowing decoupled services (like ClientInventoryService) to handle specific component logic.
 */
export class ClientWorldService {
  private world: ClientWorld;

  private translators: Map<string, ComponentDataTranslator> = new Map();
  private entityCreationListeners: EntityCreationListener[] = [];
  private worldResetListeners: WorldResetListener[] = [];

  constructor(defaults: GetDefaults<ClientRegistry> = ClientDefaults) {
    this.world = new ClientWorld(defaults);
  }

  /**
   * Get the ClientWorld instance.
   */
  getWorld(): ClientWorld {
    return this.world;
  }

  /**
   * Reset the service (for testing).
   */
  reset(defaults: GetDefaults<ClientRegistry> = ClientDefaults): void {
    this.world = new ClientWorld(defaults);
    // this.translators.clear();
    // Do not clear listeners as services (like ClientInventoryService) are long-lived
    // and registered once at startup.
    // this.entityCreationListeners = [];
    // this.worldResetListeners = [];
  }

  /**
   * Get the client entity ID for a given server entity ID.
   */
  getClientEntityId(serverEntityId: number): number | undefined {
    return this.world.getClientEntityId(serverEntityId);
  }

  /**
   * Register a translator for a specific component type.
   * Used to resolve server entity IDs within component data before adding to ClientWorld.
   */
  registerTranslator(componentName: string, translator: ComponentDataTranslator): void {
    this.translators.set(componentName, translator);
  }

  /**
   * Register a listener for entity creation events.
   * Used for deferred resolution of dependencies (e.g. items waiting for container).
   */
  registerEntityCreationListener(listener: EntityCreationListener): void {
    this.entityCreationListeners.push(listener);
  }

  /**
   * Register a listener for world reset events.
   */
  registerWorldResetListener(listener: WorldResetListener): void {
    this.worldResetListeners.push(listener);
  }

  syncWorld(worldState: EntityStatePayload[], renderService: RenderService): void {
    this.world.resetWorld();

    // Notify listeners to clear state (e.g. pending items)
    for (const listener of this.worldResetListeners) {
      listener();
    }

    if (renderService.stage) {
      renderService.stage.removeChildren();
    }
    for (const entitySnapshot of worldState) {
      this.createNetworkedEntity(entitySnapshot.entityId, entitySnapshot.components);
    }
  }

  createNetworkedEntity(entityId: number, components: { [key: string]: unknown }): number {
    const clientEntityId = this.world.registerNetworkedEntity(entityId);

    // If the entity already existed (e.g. re-entering scene), we need to clean up some state
    // so it doesn't interpolate from old positions (ghosting effect).

    // 1. Remove VisualPosition to force SmoothingSystem to re-initialize it from GlobalTransform
    this.world.removeComponent(clientEntityId, 'VisualPosition');

    // 2. Remove MarkedForDestruction if it was pending destruction
    this.world.removeComponent(clientEntityId, 'MarkedForDestruction');

    this.applyComponents(clientEntityId, components);

    // Notify listeners (e.g. to resolve pending inventory slots)
    for (const listener of this.entityCreationListeners) {
      listener(entityId, clientEntityId);
    }

    return clientEntityId;
  }

  destroyEntity(entityId: number): void {
    this.world.markNetworkedEntityForDestruction(entityId);
  }

  addComponent(serverEntityId: number, componentName: string, data: unknown): void {
    const clientEntityId = this.world.getClientEntityId(serverEntityId);
    if (clientEntityId === undefined) {
      console.warn(`Component added for unknown server entity ${serverEntityId}`);
      return;
    }

    // Apply translation if needed
    const translator = this.translators.get(componentName);
    const translatedData = translator ? translator(data, clientEntityId) : data;

    const componentInstance = this.world.createComponent(
      componentName as keyof ClientRegistry,
      translatedData as ClientRegistry[keyof ClientRegistry],
    );
    if (componentInstance) {
      this.world.addComponent(clientEntityId, componentInstance);
    } else {
      console.warn(`Unknown component class for name: ${componentName}`);
    }
  }

  removeComponent(serverEntityId: number, componentName: string): void {
    const clientEntityId = this.world.getClientEntityId(serverEntityId);
    if (clientEntityId !== undefined) {
      this.world.removeComponent(clientEntityId, componentName as keyof ClientRegistry);
    }
  }

  updateComponent(serverEntityId: number, componentName: string, data: unknown): void {
    const clientEntityId = this.world.getClientEntityId(serverEntityId);
    if (clientEntityId === undefined) {
      console.warn(`Component update for unknown server entity ${serverEntityId}`);
      return;
    }

    const component = this.world.getComponent(
      clientEntityId,
      componentName as keyof ClientRegistry,
    );
    if (component) {
      const translator = this.translators.get(componentName);
      const translatedData = translator ? translator(data, clientEntityId) : data;

      this.world.updateComponent(
        clientEntityId,
        component,
        translatedData as ClientRegistry[keyof ClientRegistry],
      );
    }
  }

  /**
   * Apply components from server payload to a client entity.
   * Handles generic component creation and specifically managing generic hierarchy (ChildOf).
   */
  private applyComponents(clientEntityId: number, components: { [key: string]: unknown }): void {
    for (const [componentName, data] of Object.entries(components)) {
      let translatedData = data;

      // Handle Generic Hierarchy (ChildOf/Children) - Core ECS feature, so stays here
      if (componentName === 'ChildOf' && this.isChildOfData(data)) {
        const clientParentId = this.world.getClientEntityId(data.parent);
        if (clientParentId !== undefined) {
          translatedData = { ...data, parent: clientParentId };
          // Rebuild parent's Children component with correct client IDs
          this.ensureChildInParent(clientParentId, clientEntityId);
        } else {
          console.warn(`ChildOf: parent server entity ${data.parent} not found on client`);
        }
      } else if (componentName === 'Children') {
        // Skip server-sent Children - we rebuild it when processing ChildOf
        continue;
      } else {
        // Check for specific translators (e.g. Inventory, Equipment)
        const translator = this.translators.get(componentName);
        if (translator) {
          translatedData = translator(data, clientEntityId) as typeof data;
        }
      }

      const componentInstance = this.world.createComponent(
        componentName as keyof ClientRegistry,
        translatedData as ClientRegistry[keyof ClientRegistry],
      );
      if (!componentInstance) {
        console.warn(`Unknown component class for name: ${componentName}`);
        continue;
      }
      this.world.addComponent(clientEntityId, componentInstance);
    }
  }

  /**
   * Type guard for ChildOf component data.
   */
  private isChildOfData(data: unknown): data is { parent: number } {
    return data !== null && typeof data === 'object' && 'parent' in data;
  }

  /**
   * Ensure a child entity is listed in the parent's Children component.
   * Creates the Children component if it doesn't exist.
   */
  private ensureChildInParent(parentId: number, childId: number): void {
    const childrenComp = this.world.getComponent(parentId, 'Children') as
      | ({ readonly typeName: 'Children' } & { children: number[] })
      | undefined;

    if (childrenComp) {
      if (!childrenComp.children.includes(childId)) {
        childrenComp.children.push(childId);
      }
    } else {
      const newChildren = this.world.createComponent('Children', { children: [childId] });
      this.world.addComponent(parentId, newChildren);
    }
  }
}
